import { useTranslation } from 'next-i18next'

import { useCallback, useEffect } from 'react'
import { useRouter } from 'next/router'

import { Link } from '~/components/Link'

import mathTopicAnalytics from '~/analytics/mathTopics/mathTopicAnalytics'

import { MathTopicData, TopicData } from '~/MathTopics/types/math-topic.type'
import { getAspectRatio } from '~/MathTopics/utils/math-topic.utils'
import ResponsiveImage from '~/components/Image/ResponsiveImage'

interface TopicProps {
  mathTopicData: MathTopicData
}

export default function Topics(props: TopicProps): React.ReactElement {
  const { mathTopicData } = props

  const { t } = useTranslation('imageAlt')

  const router = useRouter()

  const scrollToSection = useCallback((sectionId: string) => {
    const section = document.getElementById(sectionId)
    if (section) section.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    const hashFragment = router.asPath.split('#')[1]
    if (hashFragment) {
      mathTopicAnalytics.clickMainMathTopic({
        topic: hashFragment,
      })
      scrollToSection(`math-topic-${hashFragment}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath])

  return (
    <div className="flex flex-col md:flex-row items-start md:items-center justify-center mb-7">
      {mathTopicData &&
        Object.entries(mathTopicData).map(([topicId, topic]: [string, TopicData]) => {
          const aspectRatio = getAspectRatio(topicId)
          const mediaQuery = [{ screen: '(min-width: 280px)', width: `${aspectRatio.width}px` }]
          return (
            <Link
              className="flex md:flex-col items-center justify-center md:w-35 text-center"
              key={`math-topic-details-${topicId}`}
              onClick={() => scrollToSection(`math-topic-${topicId}`)}
              to={`#${topicId}`}
            >
              <ResponsiveImage
                alt={`${topic.title}-${t('icons.icon')}`}
                height={aspectRatio.height}
                imageClassName={`w-17 md:w-auto h-auto ${
                  topicId === 'algebra' ? 'md:h-22.5 mb-2 md:mb-3.5' : 'md:h-17 mb-3.5'
                }`}
                imageWidths={`${aspectRatio.width}`}
                mediaQuery={mediaQuery}
                srcKey={`math-topics/${topicId}-v3.png`}
                width={aspectRatio.width}
              />
              <div
                className={`flex ${
                  topicId !== 'algebra' ? 'items-center' : ''
                } h-8 ml-5 md:ml-0 text-black-light-fade font-medium text-base leading-4 tracking-wide`}
              >
                {topic.title}
              </div>
            </Link>
          )
        })}
    </div>
  )
}
